import {Injectable} from '@angular/core';
import {CardConfig} from '@app/model/dcp-config-service/card-config';
import {PlaceOrderRequestBody} from '@app/model/order/dcp-place-order-body.model';
import {CustomerPortalResponse} from '@app/model/psp-integration/customer-portal-response.model';
import {PspSetupIntentResponse} from '@app/model/psp-integration/dcp-setup-intent-response';
import {DcpSetupIntent} from '@app/model/stripe/setup-intent';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpApiService, DcpGlobalMessageService, JsonService, STRIPE_CUSTOMER_IDS} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Store} from '@ngrx/store';
import {Cart} from '@spartacus/cart/base/root';
import {PaymentDetails, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {loadStripe, PaymentIntent, PaymentIntentResult, PaymentMethod, SetupIntent} from '@stripe/stripe-js';
import {BehaviorSubject, combineLatest, from, Observable, of, throwError} from 'rxjs';
import {catchError, concatMap, filter, first, map, switchMap, take, takeUntil, tap, toArray} from 'rxjs/operators';
import {PaymentProvider} from '../dcp-cc-psp.service';
import {DcpCheckoutPaymentService} from '../dcp-checkout-payment.service';
import {PaymentProviderService} from '../payment-provider.service';
import * as stripeActions from '../store/actions/stripe.action';
import {StripePaymentState} from '../store/payment-state';
import * as fromStripe from '../store/reducers/stripe.reducer';
import {StripePaymentErrorService} from './error-handling/stripe-payment-error.service';
import {ThreeDsService} from '@app/services/three-ds/three-ds.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpStripeService extends PaymentProviderService {

  private isStripeOpen$ = new BehaviorSubject<boolean>(false);
  private stripeCustomerId: string;

  constructor(private apiService: DcpApiService,
              private activeCartService: DcpActiveCartService,
              private dcpCheckoutPaymentService: DcpCheckoutPaymentService,
              private jsonService: JsonService,
              private userAccountFacade: UserAccountFacade,
              private store: Store,
              private dcpGlobalMessageService: DcpGlobalMessageService,
              private stripePaymentErrorService: StripePaymentErrorService,
              private threeDsService: ThreeDsService,
  ) {
    super();
  }

  getStripeCardConfig(): Observable<CardConfig> {
    return this.activeCartService.getActiveCart().pipe(
      take(1),
      switchMap((cart) => {
        const {vendor, salesOrg} = this.dcpCheckoutPaymentService.getVendorSalesOrg(cart);
        return this.dcpCheckoutPaymentService.getCardConfig(vendor, salesOrg, PaymentProvider.STRIPE);
      })
    );
  };

  fetchSetupIntentSecret(): Observable<string> {
    return combineLatest(
      this.activeCartService.getActiveCart(),
      this.userAccountFacade.get(),
    ).pipe(
      first(),
      switchMap(
        ([cart, user]: [Cart, User]) =>
          this.apiService.callKyma<PspSetupIntentResponse>(
            'pspIntegration', 'setupIntent', {}, {
              body: this.getFetchSetupIntentBody(cart, user)
            }, 'POST')
      ),
      // save stripe customer id to be used in next setup intent call
      tap(setupIntent => this.stripeCustomerId = setupIntent.stripeCustomerId),
      map(setupIntent => setupIntent.clientSecret)
    );
  }

  /**
   * Creates Stripe payment intent
   */
  createPaymentIntent(): Observable<PlaceOrderRequestBody> {
    return combineLatest([
      this.activeCartService.getActiveCart(),
      this.userAccountFacade.get()
    ]).pipe(
      first(),
      switchMap(
        ([cart, user]: [Cart, User]) =>
          this.apiService.callKyma<PlaceOrderRequestBody>(
            'pspIntegration', 'paymentIntent', {userId: user.uid}, {
              body: this.getCreateIntentBody(cart, user)
            }, 'POST')
      )
    );
  }

  /**
   * Creates Billing(customer-portal) session in stripe
   *
   */
  createCustomerPortalSession(): Observable<CustomerPortalResponse> {
    return this.userAccountFacade.get().pipe(
      first((user: User) => !!user?.orgUnit?.uid),
      switchMap((user: User) =>
        this.apiService.callKyma<CustomerPortalResponse>(
          'pspIntegration', 'customerPortal', {b2bUnitId: user.orgUnit.uid}, {}, 'POST')
      )
    );
  }

  saveCreditCardDetails(user: User, setupIntentWithPaymentMethod: SetupIntent): Observable<any> {
    const preparedPaymentData = this.preparePaymentDetails(user, setupIntentWithPaymentMethod);
    return this.dcpCheckoutPaymentService.createFullCreditCardDetails(preparedPaymentData).pipe(untilDestroyed(this));
  }

  preparePaymentDetails(user: User, setupIntent: DcpSetupIntent): PaymentDetails {
    const paymentDetails = this.dcpCheckoutPaymentService.prepareBillingAddress(user);
    return {
      ...paymentDetails,
      cardNumber: this.getMaskedCardNumber(setupIntent.payment_method as PaymentMethod),
      cardType: {
        code: (setupIntent.payment_method as PaymentMethod).card.brand,
        name: (setupIntent.payment_method as PaymentMethod).card.brand.toLowerCase().replace(/^\w/, (firstChar) => firstChar.toUpperCase()),
      },
      cvn: null,
      defaultPayment: true,
      expiryMonth: (setupIntent.payment_method as PaymentMethod).card.exp_month + '',
      expiryYear: (setupIntent.payment_method as PaymentMethod).card.exp_year + '',
      saved: true,
      subscriptionId: (setupIntent.payment_method as PaymentMethod).id,
      paymentProvider: PaymentProvider.STRIPE,
      ...({dpaoSubscriptionId: setupIntent.dpaoSubscriptionId}),
    };
  }

  getSalesOrg(cart: Cart): string {
    return this.dcpCheckoutPaymentService.extractConsignmentsMetaData(cart)[0].salesOrg;
  }

  getMaskedCardNumber(paymentMethod: PaymentMethod): string {
    return `${paymentMethod.id}-${paymentMethod.card.last4}`;
  }

  openAddNewCreditCardComponent(currency: string = undefined): void {
    this.isStripeOpen$.next(true);
  }

  closeAddNewCreditCardComponent(): void {
    this.isStripeOpen$.next(false);
  }

  setPspMerchantId(cart: Cart): void {
    return;
  }

  isAddNewCreditCardComponentOpened(): Observable<boolean> {
    return this.isStripeOpen$;
  }

  convertStateToPlaceOrderPayload(): Observable<PlaceOrderRequestBody> {
    return this.getPaymentState().pipe(
      // ensure successfully paymentIntent call
      filter((state) => this.validatePaymentState(state)),
      first(),
      map(state => state.data
      )
    );
  }

  startCcFlow(): void {
    this.store.dispatch(new stripeActions.StartStripePaymentFlow());
  }

  validatePaymentState(state: StripePaymentState): boolean {
    if (!state) {
      throw new Error('Stripe state is undefined');
    }
    if (state.loading) {
      return false;
    }
    if (state.error) {
      throw state;
    }
    return true;
  }

  displayPaymentError(state: StripePaymentState) {
    this.stripePaymentErrorService.getMessageForStripe(state).pipe(first(), untilDestroyed(this))
      .subscribe(message => {
        this.dcpGlobalMessageService.addMessage(message, this.constructor.name, 'errorGlobal');
      });
  }

  /**
   * errors handled by psp-lambda
   */
  saveAuthorizationErrors(state: StripePaymentState): void {
    return;
  }

  invoke3DS(paymentIntents: PaymentIntent[]): Observable<PaymentIntentResult[]> {
    if (!paymentIntents.length) {
      return of([]);
    }

    let cardConfigRef: CardConfig;
    return this.getStripeCardConfig().pipe(
      switchMap(cardConfig => {
        cardConfigRef = cardConfig;
        return loadStripe(cardConfig.merchantId);
      }),
      switchMap(stripe =>
        from(paymentIntents).pipe(
          concatMap(paymentIntent =>
            from(stripe.confirmCardPayment(paymentIntent.client_secret)).pipe(
              switchMap(threeDsResult =>
                threeDsResult.error ? throwError(() => threeDsResult) : of(threeDsResult)
              ),
              takeUntil(
                this.threeDsService.cancelThreeDs$().pipe(
                  switchMap(() => throwError(() => '3DS operation cancelled by user'))
                )
              )
            )
          ),
          toArray(),
          catchError(error => this.cancelPaymentIntents(paymentIntents.map(pi => pi.id), cardConfigRef).pipe(
            switchMap(() => throwError(() => error))
          ))
        )
      )
    );
  };

  cancelPaymentIntents(paymentIntentIds: string[], {
    merchantId,
    connectedAccountId = undefined
  }): Observable<{ cancelledPaymentIntents: string[] }> {
    return this.userAccountFacade.get().pipe(
      first(),
      switchMap(user => {
        const body = {
          paymentIntentIds: paymentIntentIds.map(id => this.wrapPaymentIntent(id, merchantId, connectedAccountId))
        };
        return this.apiService.callKyma<{ cancelledPaymentIntents: string[] }>(
          'pspIntegration',
          'cancelPaymentIntent',
          {userId: user.uid}, {body}, 'POST'
        );
      })
    );
  }

  wrapPaymentIntent(id: string, merchantId: string, connectedAccountId: string) {
    return {
      id,
      merchantId,
      connectedAccountId
    };
  }

  private getPaymentState(): Observable<StripePaymentState> {
    return this.store.select(fromStripe.getState);
  }

  private getCreateIntentBody(cart: Cart, user: User): {} {
    const {salesOrg} = this.dcpCheckoutPaymentService.getVendorSalesOrg(cart);
    const stripeCustomerIds = this.jsonService.getJsonDataValue(user.json, STRIPE_CUSTOMER_IDS);
    const stripeCustomerId = stripeCustomerIds ? stripeCustomerIds[salesOrg] : this.stripeCustomerId;
    return {
      cartId: this.activeCartService.getCartIdByUserId(cart, user.uid),
      customerId: user.customerId,
      paymentMethodId: cart.paymentInfo?.subscriptionId,
      stripeCustomerId
    };
  }

  private getFetchSetupIntentBody(cart: Cart, user: User): {} {
    const {salesOrg, vendor: vendorId} = this.dcpCheckoutPaymentService.getVendorSalesOrg(cart);
    const stripeCustomerIds = this.jsonService.getJsonDataValue(user.json, STRIPE_CUSTOMER_IDS);
    const stripeCustomerId = stripeCustomerIds ? stripeCustomerIds[salesOrg] : this.stripeCustomerId;

    return {
      userId: user.uid,
      customerId: user.customerId,
      salesOrg,
      vendorId,
      currency: cart.totalPrice?.currencyIso,
      ...(stripeCustomerId && {pspCustomerId: stripeCustomerId})
    };
  }
}
